import { useMemo, useState } from 'react';
import { MenuResponse } from '../../../context/models/accounts/MenuResponse';
import { useAuth } from '../../../utils/AuthProvider';
import { NavLink, matchPath, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
	ChevronDown,
	ChevronRight,
	Folder,
	LayoutDashboard,
	Newspaper,
	Package,
	Shapes,
	Image,
	Users,
	KeyRound,
	Tag,
	type LucideIcon,
} from 'lucide-react';

interface MenuTree {
	id: number;
	name: string;
	path: string;
	icon: string;
	parentId: number;
	sortOrder: number;
	isActive: boolean;
	childs?: MenuTree[];
}

const buildTree = (parentId: number, menus: MenuResponse[]) => {
	return menus
		.filter((m) => m.parentId === parentId)
		.map((menu) => {
			const menuTree: MenuTree = {
				...menu,
				isActive: false,
				childs: buildTree(menu.id, menus),
			};
			return menuTree;
		});
};

export const MenuRender = () => {
	const { menus } = useAuth();
	const menuTrees: MenuTree[] = useMemo(() => {
		return menus ? buildTree(0, menus) : [];
	}, [menus]);

	return (
		<ul className='flex w-full flex-col gap-1 px-2'>
			{menuTrees && menuTrees.length > 0 && menuTrees.map((group) => <MenuItem key={group.id} menu={group} isRoot={true} isCollapsed={false} />)}
		</ul>
	);
};

export const MenuRenderCollapsed = () => {
	const { menus } = useAuth();
	const menuTrees: MenuTree[] = useMemo(() => {
		return menus ? buildTree(0, menus) : [];
	}, [menus]);

	return (
 		<ul className='flex w-full flex-col items-center gap-2 px-2'>
			{menuTrees && menuTrees.length > 0 && menuTrees.map((group) => <MenuItem key={group.id} menu={group} isRoot={true} isCollapsed={true} />)}
		</ul>
	);
};

const MenuSub = ({ menus, isOpen }: { menus: MenuTree[]; isOpen: boolean }) => {
	if (!isOpen || !menus || menus.length === 0) return null;
	return (
		<ul className='ml-6 mt-1 flex flex-col gap-1 border-l border-border/70 pl-2'>
			{menus.map((menu) => (
				<MenuItem key={menu.id} menu={menu} isRoot={false} isCollapsed={false} />
			))}
		</ul>
	);
};

const iconByGroupName: Array<{ keyword: string; icon: LucideIcon }> = [
	{ keyword: 'dashboard', icon: LayoutDashboard },
	{ keyword: 'tài khoản', icon: Users },
	{ keyword: 'account', icon: Users },
	{ keyword: 'quyền', icon: KeyRound },
	{ keyword: 'vai trò', icon: KeyRound },
	{ keyword: 'tin bài', icon: Newspaper },
	{ keyword: 'bài viết', icon: Newspaper },
	{ keyword: 'sản phẩm', icon: Package },
	{ keyword: 'product', icon: Package },
	{ keyword: 'danh mục', icon: Tag },
	{ keyword: 'collection', icon: Shapes },
	{ keyword: 'bộ sưu tập', icon: Shapes },
	{ keyword: 'banner', icon: Image },
];

const getRootIcon = (menuName: string): LucideIcon => {
	const lower = menuName.toLowerCase();
	const matched = iconByGroupName.find((item) => lower.includes(item.keyword));
	return matched?.icon || Folder;
};

const MenuItem = ({ menu, isRoot, isCollapsed }: { menu: MenuTree; isRoot: boolean; isCollapsed: boolean }) => {
	const location = useLocation();
	const currentActive = useMemo(() => {
		return (matchPath({ path: menu.path, end: false }, location.pathname) != null && menu.path !== '/') || (menu.path === '/' && location.pathname === '/');
	}, [location, menu]);

	const isChildActive = useMemo(() => {
		if (!menu.childs) return false;
		return menu.childs.some((child) => location.pathname.includes(child.path));
	}, [location, menu.childs]);

	const [isExpanded, setIsExpanded] = useState(isChildActive || currentActive);

	const hasChild = !!menu.childs && menu.childs.length > 0;
	const RootIcon = isRoot ? getRootIcon(menu.name) : null;

	if (isCollapsed && isRoot) {
		return (
			<li className='w-full'>
				<NavLink
					to={menu.path}
					title={menu.name}
					className={cn(
						'group mx-auto flex h-10 w-10 items-center justify-center rounded-xl border transition-all',
						currentActive ? 'border-primary/30 bg-primary/15 text-primary shadow-sm' : 'border-transparent text-muted-foreground hover:border-border/80 hover:bg-muted hover:text-foreground'
					)}
				>
					{RootIcon ? <RootIcon className='h-5 w-5' /> : <Folder className='h-5 w-5' />}
				</NavLink>
			</li>
		);
	}

	return (
		<li className='w-full'>
			<NavLink
				to={hasChild ? '#' : menu.path}
				title={menu.name}
				onClick={(e) => {
					if (hasChild) {
						e.preventDefault();
						setIsExpanded((prev) => !prev);
					}
				}}
				className={cn(
					'group relative flex items-center justify-between gap-3 overflow-hidden rounded-xl px-3 py-2 text-sm transition-all',
					currentActive && !hasChild ? 'bg-primary/12 font-medium text-primary ring-1 ring-primary/20' : 'text-muted-foreground hover:bg-muted/80 hover:text-foreground',
					isRoot ? 'font-medium' : 'font-normal'
				)}
			>
				<div className='flex items-center gap-3'>
					{isRoot && (
						<span
							className={cn(
								'flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-background shadow-sm',
								currentActive && !hasChild ? 'border-primary/20 bg-primary/5 text-primary' : 'group-hover:border-border'
							)}
						>
							{RootIcon ? <RootIcon className='h-4 w-4' /> : <Folder className='h-4 w-4' />}
						</span>
					)}
					<span className='truncate'>{menu.name}</span>
				</div>
				{hasChild && (
					<span className='opacity-50'>
						{isExpanded ? <ChevronDown className='h-4 w-4' /> : <ChevronRight className='h-4 w-4' />}
					</span>
                )}
			</NavLink>
			{hasChild && <MenuSub menus={menu.childs!} isOpen={isExpanded} />}
		</li>
	);
};
